import React, { Component } from 'react';
import {
  ForgeButton,
  ForgeCheckbox,
  ForgeIcon,
  ForgeIconButton,
  ForgeInlineMessage,
  ForgeSelect,
  ForgeTextField,
  ForgeTooltip,
} from '@tylertech/forge-react';
import CopyToClipboard from 'common/components/CopyToClipboard';
import { map } from 'lodash';
import { fetchTranslation } from 'common/locale';
import { ResourceType } from './ExportModalTypes';

const t = (k: string) => fetchTranslation(k, 'shared.explore_grid.export_dataset_modal');

interface Props {
  onCopy: () => void;
  resources: ResourceType[];
  onSelectedUrlChange: (type: ResourceType) => void;
  selectedResource: ResourceType;
  filteringRadioButtons: JSX.Element | null;
  versionRadioButtons?: JSX.Element | null;
  useResourceName: boolean;
  /** Only pass in if you have a resource name available. */
  onResourceNameUpdate?: (checked: boolean) => void;
  showApiLimitWarning: boolean;
  isOdata: boolean;
  viewUid: string;
}

export default class UrlEndpoint extends Component<Props> {
  renderResourceSelect(apiOrOdata: string): JSX.Element {
    const { resources, selectedResource, onSelectedUrlChange, isOdata } = this.props;

    const options = map(resources, (type, index) => ({
      label: type.label,
      value: type,
      elementAttributes: new Map([
        ['data-testId', `${apiOrOdata}-endpoint-data-format-selector-option-${index}`],
      ]),
    }));

    return (
      <ForgeSelect
        data-testid={`${apiOrOdata}-endpoint-data-format-selector`}
        onChange={(event: CustomEvent) => onSelectedUrlChange(event.detail)}
        options={options}
        label={t(isOdata ? 'odata_version' : 'data_format')}
        className="forge-select export-formats"
        selectedIndex={resources.findIndex((opt: ResourceType) => opt.url === selectedResource.url)}
      />
    );
  }

  renderAPIDocumentationLinks(): JSX.Element {
    const foundryUrl = `https://dev.socrata.com/foundry/${window.location.host}/${this.props.viewUid}`;
    return (
      <div className="api-documentation-links">
        <ForgeButton className="foundry-link">
          <a data-testid="api-foundry-doc-link" href={foundryUrl} target="_blank" rel="noreferrer">
            <span>{t('api_documentation_link_label')}</span>
            <ForgeIcon name="open_in_new" />
          </a>
        </ForgeButton>
        <ForgeButton>
          <a data-testid="api-dev-doc-link" href="https://dev.socrata.com" target="_blank" rel="noreferrer">
            <span>{t('developer_portal_link_label')}</span>
            <ForgeIcon name="open_in_new" />
          </a>
        </ForgeButton>
      </div>
    );
  }

  renderOdataDocumentationLinks(): JSX.Element {
    const odataDoc =
      'https://support.socrata.com/hc/en-us/articles/115005364207-Access-Data-Insights-Data-using-OData';
    return (
      <div className="odata-documentation-links">
        <ForgeButton className="foundry-link">
          <a data-testid="odata-foundry-doc-link" href={odataDoc} target="_blank" rel="noreferrer">
            <span>{t('odata_documentation_link_label')}</span>
            <ForgeIcon name="open_in_new" />
          </a>
        </ForgeButton>
      </div>
    );
  }

  /** Renders resource name checkbox if onResourceNameUpdate handler provided. */
  renderUseResourceName(): JSX.Element | null {
    const { onResourceNameUpdate, useResourceName } = this.props;
    return onResourceNameUpdate ? (
      <ForgeCheckbox
        className="resource-name-checkbox"
        data-testid="use-resource-name-checkbox"
        id="resource-checkbox-field"
        checked={useResourceName}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onResourceNameUpdate(event.target.checked)}
      >
        {t('use_resource_name')}
      </ForgeCheckbox>
    ) : null;
  }

  renderApiLimitMessage(): JSX.Element | null {
    return this.props.showApiLimitWarning ? (
      <ForgeInlineMessage theme="warning" className="api-limit-message">
        <ForgeIcon name="warning" slot="icon" />
        <div slot="title">{t('api_limit_title')}</div>
        <div dangerouslySetInnerHTML={{ __html: t('api_limit_body') }} />
      </ForgeInlineMessage>
    ) : null;
  }

  render() {
    const { filteringRadioButtons, isOdata, selectedResource, onCopy, versionRadioButtons } = this.props;
    const apiOrOdata = isOdata ? 'odata' : 'api';
    const docLinks = isOdata ? this.renderOdataDocumentationLinks() : this.renderAPIDocumentationLinks();

    return (
      <div className="resource-toggle endpoint">
        {this.renderResourceSelect(apiOrOdata)}
        {filteringRadioButtons}
        {!isOdata && this.renderApiLimitMessage()}
        {versionRadioButtons ?? null}
        <ForgeTextField>
          <input
            type="text"
            data-testid={`${apiOrOdata}-endpoint-input`}
            id={`${apiOrOdata}-endpoint`}
            value={selectedResource.url || ''}
            readOnly={true}
          />
          <label htmlFor={`${apiOrOdata}-endpoint`}>{t(`${apiOrOdata}_endpoint`)}</label>
          <span slot="end">
            <CopyToClipboard text={selectedResource.url} onCopy={onCopy}>
              <ForgeIconButton data-testid={`inline-${apiOrOdata}-copy`} aria-label={t('copy_clipboard')}>
                <ForgeIcon name="content_copy" />
                <ForgeTooltip placement={'bottom'}>{t('copy_clipboard')}</ForgeTooltip>
              </ForgeIconButton>
            </CopyToClipboard>
          </span>
        </ForgeTextField>
        {this.renderUseResourceName()}
        {docLinks}
      </div>
    );
  }
}
